import { ComponentProps, useEffect, useRef, useState } from 'react';
import Drawer from 'antd/es/drawer';
import clsx from 'clsx';

import { OpSpin } from '../OpSpin/OpSpin';

import './OpDrawer.scss';

interface OpDrawerProps extends ComponentProps<typeof Drawer> {
  fillBodyHeight?: boolean;
}

/**
 * OpDrawer is a wrapper around Ant Design's Drawer component.
 * It provides a panel that slides in from the edge of the screen to display additional content.
 *
 * Use this component for navigation menus, detailed information, or forms that don't need to block
 * the entire page. Includes a loading optimization to handle drawers with heavy content.
 * Commonly used with query parameters to control open/close state.
 *
 * @example
 * <OpDrawer
 *   open={isOpen}
 *   onClose={handleClose}
 *   title="Drawer Title"
 *   placement="right"
 * >
 *   Drawer content
 * </OpDrawer>
 *
 */
export const OpDrawer = ({
  children,
  placement = 'right',
  className = '',
  maskClassName = '',
  fillBodyHeight = false,
  onClose,
  ...drawerProps
}: OpDrawerProps) => {
  const classes = clsx('op-drawer', className, {
    'op-drawer--fill-body-height': fillBodyHeight,
  });

  const maskClasses = clsx('op-drawer-mask', maskClassName);

  const drawerOpenTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  /**
   * Antd Drawer has what I would consider a bug where if there is a ton of content to
   * display in the drawer, it will not open the drawer until all is processed. This is
   * a workaround to show nothing until the drawer is ready to be displayed.
   * In most cases this will visual not affect anything
   */
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!drawerProps.open) return;

    drawerOpenTimeoutRef.current = setTimeout(() => {
      setLoading(false);
    }, 50); // Played with different values but below 50ms is the lowest I can get it to work

    return () => {
      setLoading(true);
      if (drawerOpenTimeoutRef.current) {
        clearTimeout(drawerOpenTimeoutRef.current);
        drawerOpenTimeoutRef.current = null;
      }
    };
  }, [drawerProps.open]);

  return (
    <Drawer
      placement={placement}
      className={classes}
      maskClassName={maskClasses}
      onClose={onClose}
      {...drawerProps}
    >
      {loading ? <OpSpin centered /> : children}
    </Drawer>
  );
};
